import React, { useEffect, useRef, useState } from "react";
import {
  isCapture, setCaptureLabel, downloadCaptureTrace, getCaptureData, uploadCaptureSummary,
} from "../poseRecorder.ts";

// ?capture=1 - a cued, labeled recording session for the QA fake-pose
// injector. STANDALONE (Joshua 2026-08-29): the game never renders in this
// mode - black stage, big cues. Time is tracked with real timestamps (the
// old nested-setState countdown collapsed every phase to ~1s), each phase
// gets a 3-2-1 lead-in, recording waits until a pose is actually detected,
// and the session ends with keypoint-over-time graphs per move.
const PHASES: [string, string, number][] = [
  // [label, on-screen cue, seconds]
  // Cues use the GAME's own move language (Joshua 2026-08-31): running is
  // rapid SHALLOW squat-bounces, not knees-up jogging
  ["idle", "STAND STILL", 15],
  ["jog", "RAPID SHORT SQUATS - shallow bounces, like running in the game", 45],
  ["jog_fast", "BOUNCE FASTER - top speed!", 30],
  ["squat", "DEEP SQUAT BURSTS - one deep squat, stand, repeat (the BOOST)", 45],
  ["slash", "SLASH - big arm swings, both arms", 45],
  ["crouch", "CROUCH AND HOLD - like dodging", 15],
  ["idle2", "STAND STILL - catch your breath", 10],
];
const COUNTDOWN_S = 4;

type Stage = "waitpose" | "count" | "record" | "done";

const CaptureSession: React.FC = () => {
  const [stage, setStage] = useState<Stage>("waitpose");
  const [phase, setPhase] = useState(0);
  const [left, setLeft] = useState(0);
  const [paused, setPaused] = useState(false);
  const [poseSeen, setPoseSeen] = useState(0);
  const stageEndsAt = useRef(0);
  const pausedAt = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Gate on a real pose: 15 consecutive frames before anything starts
  useEffect(() => {
    if (!isCapture) return;
    let streak = 0;
    const onPose = (e: Event) => {
      const d = (e as CustomEvent).detail as number[];
      streak = d && d.length > 100 ? streak + 1 : 0;
      setPoseSeen(streak);
    };
    window.addEventListener("poseData", onPose);
    return () => window.removeEventListener("poseData", onPose);
  }, []);

  useEffect(() => {
    if (!isCapture) return;
    const id = window.setInterval(() => {
      if (paused) return;
      const now = performance.now();
      setStage((st) => {
        if (st === "waitpose") {
          if (poseSeenRef.current >= 15) {
            stageEndsAt.current = now + COUNTDOWN_S * 1000;
            return "count";
          }
          return st;
        }
        if (st === "done") return st;
        const remain = (stageEndsAt.current - now) / 1000;
        setLeft(Math.max(0, Math.ceil(remain)));
        if (remain > 0) return st;
        if (st === "count") {
          setCaptureLabel(PHASES[phaseRef.current][0]);
          stageEndsAt.current = now + PHASES[phaseRef.current][2] * 1000;
          return "record";
        }
        // record finished -> next phase or done
        setCaptureLabel("");
        if (phaseRef.current + 1 >= PHASES.length) {
          finish();
          return "done";
        }
        setPhase(phaseRef.current + 1);
        stageEndsAt.current = now + COUNTDOWN_S * 1000;
        return "count";
      });
    }, 200);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  // refs mirror state the interval closure needs fresh
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const poseSeenRef = useRef(poseSeen);
  poseSeenRef.current = poseSeen;

  const finish = () => {
    downloadCaptureTrace();
    // Graphs: hip height + wrist speed over time, one strip per phase
    window.setTimeout(() => {
      const cv = canvasRef.current;
      if (!cv) return;
      drawSummary(cv);
      try { uploadCaptureSummary(cv.toDataURL("image/png")); } catch { /* best effort */ }
    }, 300);
  };

  const drawSummary = (cv: HTMLCanvasElement) => {
    const { frames, phases } = getCaptureData();
    // boundaries come from ALL marks ("" = countdown gap ends the segment)
    const rows = phases
      .map((p, i) => ({ label: p[1], t0: p[0], t1: i + 1 < phases.length ? phases[i + 1][0] : Infinity }))
      .filter((p) => p.label);
    const W = 640, RH = 80;
    cv.width = W; cv.height = rows.length * RH + 30;
    const g = cv.getContext("2d");
    if (!g) return;
    g.fillStyle = "#0b1020"; g.fillRect(0, 0, cv.width, cv.height);
    g.font = "700 12px system-ui"; g.fillStyle = "#94a3b8";
    g.fillText("hip height (green) · wrist speed (orange)", 12, 18);
    rows.forEach((ph, r) => {
      const seg = frames.filter((f) => f[0] >= ph.t0 && f[0] < ph.t1);
      const y0 = 30 + r * RH;
      g.strokeStyle = "rgba(255,255,255,0.12)";
      g.strokeRect(8, y0 + 4, W - 16, RH - 12);
      g.fillStyle = "#e2e8f0"; g.font = "700 13px system-ui";
      g.fillText(ph.label.toUpperCase() + `  (${seg.length}f)`, 14, y0 + 18);
      if (seg.length < 4) return;
      // pull hipY (idx 34 image y) and right-wrist world xyz (idx 16)
      const hip: number[] = [], spd: number[] = [];
      let prev: [number, number, number, number] | null = null;
      for (const f of seg) {
        let h = 0.5; let w: [number, number, number] | null = null;
        for (let i = 1; i + 4 < f.length; i += 5) {
          if (f[i] === 34) h = f[i + 2];
          else if (f[i] === 16) w = [f[i + 1], f[i + 2], f[i + 3]];
        }
        hip.push(h);
        if (w && prev) {
          const dt = Math.max(1, f[0] - prev[3]);
          spd.push(Math.hypot(w[0] - prev[0], w[1] - prev[1], w[2] - prev[2]) / dt * 1000);
        } else spd.push(0);
        if (w) prev = [w[0], w[1], w[2], f[0]];
      }
      const plot = (vals: number[], color: string) => {
        const mn = Math.min(...vals), mx = Math.max(...vals), sp = mx - mn || 1;
        g.strokeStyle = color; g.lineWidth = 1.6; g.beginPath();
        vals.forEach((v, i) => {
          const x = 10 + (i / (vals.length - 1)) * (W - 20);
          const y = y0 + RH - 12 - ((v - mn) / sp) * (RH - 34);
          i ? g.lineTo(x, y) : g.moveTo(x, y);
        });
        g.stroke();
      };
      plot(hip.map((v) => 1 - v), "#4ade80");   // up = up
      plot(spd, "#fb923c");
    });
  };

  if (!isCapture) return null;
  const cue = PHASES[Math.min(phase, PHASES.length - 1)];
  return (
    <div
      onClick={() => stage !== "done" && stage !== "waitpose" && setPaused((p) => {
        if (!p) pausedAt.current = performance.now();
        else stageEndsAt.current += performance.now() - pausedAt.current;
        return !p;
      })}
      style={{
        position: "fixed", inset: 0, zIndex: 38,   // beneath the start gate (40): camera tap comes first
        background: "#05070f", color: "#fff",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        fontFamily: "system-ui, sans-serif", textAlign: "center",
        overflowY: "auto",
      }}
    >
      {stage === "waitpose" && (
        <>
          <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 10 }}>MOTION CAPTURE</div>
          <div style={{ fontSize: 17, opacity: 0.8, maxWidth: 420, lineHeight: 1.5 }}>
            Step back so your WHOLE BODY is in the camera view (top right).
            Recording starts when you're detected.
          </div>
          <div style={{ fontSize: 15, marginTop: 18, color: poseSeen > 0 ? "#4ade80" : "#64748b" }}>
            {poseSeen > 0 ? `detecting... ${Math.min(15, poseSeen)}/15` : "no pose detected yet"}
          </div>
        </>
      )}
      {stage === "count" && (
        <>
          <div style={{ fontSize: 15, opacity: 0.6 }}>NEXT · {phase + 1}/{PHASES.length}</div>
          <div style={{ fontSize: 30, fontWeight: 800, margin: "8px 0" }}>{cue[1]}</div>
          <div style={{ fontSize: 76, fontWeight: 800, color: "#facc15" }}>{left}</div>
        </>
      )}
      {stage === "record" && (
        <>
          <div style={{ fontSize: 15, color: "#f87171", fontWeight: 800 }}>
            ● RECORDING {phase + 1}/{PHASES.length}
          </div>
          <div style={{ fontSize: 30, fontWeight: 800, margin: "8px 0" }}>{cue[1]}</div>
          <div style={{ fontSize: 56, fontWeight: 800, color: "#facc15" }}>{left}</div>
          <div style={{ fontSize: 13, opacity: 0.5, marginTop: 12 }}>
            {paused ? "PAUSED - tap to resume" : "tap to pause"}
          </div>
        </>
      )}
      {stage === "done" && (
        <div style={{ padding: "24px 0" }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#4ade80", marginBottom: 8 }}>
            CAPTURE COMPLETE
          </div>
          <div style={{ fontSize: 14, opacity: 0.7, marginBottom: 14 }}>
            Trace downloaded + uploaded. Your moves, keypoints over time:
          </div>
          <canvas ref={canvasRef} style={{ maxWidth: "92vw", borderRadius: 10 }} />
        </div>
      )}
    </div>
  );
};

export default CaptureSession;
